// =============================================================================
// io/sync/journal.ts — journal entries, read and corrected.
// -----------------------------------------------------------------------------
// One row per (user, date). The pre-market plan, mid-session notes and EOD recap.
//
// ── 🚨 THE COLUMNS ARE NOT ALL WHAT THEY SAY (D59's pattern, third instance) ──
//
// `eod_mistakes` and `eod_wins` hold INVERTED content for any entry the old
// client has not re-saved since its fix. `domain/journal.ts` explains the history
// and `applyEodSwap` corrects it, guarded by the `_eodRecapSwapMigrated` marker
// inside `booleans` — the only thing distinguishing a corrected row from a raw
// one. Reading these two columns straight shows a trader's wins under "what
// broke", which is the exact inverse of what they wrote.
//
// ── TWO MIGRATIONS DELIBERATELY NOT PORTED ──
//
// The old client also does two WRITE-SHAPED things to journal entries on load:
//
//   1. WEEKEND RE-KEY — an entry dated Sat/Sun is moved to a weekday derived
//      from its `createdAt`, recording the old date in `originalCalendarDate`.
//      CME has no weekend session, so a Sunday-evening entry about Monday
//      belongs to Monday.
//   2. EMPTY-WEEKEND PRUNE — weekend-dated entries with nothing written are
//      dropped from the in-memory list.
//
// Both are omitted here ON PURPOSE, and this is a decision rather than an
// oversight: each changes WHICH DATE an entry belongs to. Doing that at read time
// in a second client means the same entry appears under two different dates
// depending on which app opened it, and the old client persists its version. That
// has to be a migration run once against the database, not a per-client read
// rule — recorded in the D-log rather than implemented quietly.
//
// So this reader may return weekend-dated entries the old client would have moved
// or hidden. `WEEKEND_ENTRY_CAVEAT` is exported so a surface can say so.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import {
  applyEodSwap, normalisePlan,
  type EodRecap, type JournalEntry, type MidSessionNote,
} from '../../domain/journal';
import { PAGE_ROWS } from './load';

export const JOURNAL_SELECT = [
  'date',
  'market_regime', 'gamma_regime', 'overall_bias',
  'market_regime_analysis', 'gamma_regime_analysis', 'bias_summary',
  'disqualifiers', 'pre_market_plan', 'mid_session_notes',
  'eod_what_worked', 'eod_what_broke', 'eod_mistakes', 'eod_wins',
  'eod_lessons', 'eod_tomorrow_focus', 'eod_mood', 'eod_energy',
  'booleans', 'readiness_items', 'readiness_completed_at',
  'session_contract', 'recordings', 'created_at',
  // ── 🚨 `updated_at` IS LOAD-BEARING, and omitting it disables a guard ──
  //
  // `journal_entries` is a first-class member of the v2.72.0 stale-write guard
  // (`STALE_GUARD` / `STALE_KEY_COL['journal_entries'] === 'date'`), and that
  // guard is FAIL-OPEN by design: `partitionByStaleness` returns everything as
  // `fresh` when it has no baseline map. So a read that does not select
  // `updated_at` cannot seed a baseline, every journal write classifies as fresh,
  // and two tabs on the same day silently overwrite each other's prose — no
  // error, no warning, and the old client reads the clobbered row back happily.
  // That is the exact bug v2.72.0 was written to fix, re-armed by an omission in
  // a SELECT list.
  'updated_at',
].join(',');

/** Surfaces that list journal dates should mention this. See the header. */
export const WEEKEND_ENTRY_CAVEAT =
  'Weekend-dated entries are shown as stored. The current client re-keys them to ' +
  'the next session day on load; that migration has not been ported, so a Saturday ' +
  'or Sunday entry may appear here under a date the other client would move.';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

function recapFromDb(r: Record<string, unknown>): EodRecap {
  return {
    whatWorked: str(r['eod_what_worked']),
    whatBroke: str(r['eod_what_broke']),
    mistakes: str(r['eod_mistakes']),
    wins: str(r['eod_wins']),
    lessons: str(r['eod_lessons']),
    tomorrowFocus: str(r['eod_tomorrow_focus']),
    mood: str(r['eod_mood']),
    energy: str(r['eod_energy']),
  };
}

function notesFromDb(v: unknown): MidSessionNote[] {
  if (!Array.isArray(v)) return [];
  return v.map((n, i) => {
    const o = (n && typeof n === 'object') ? n as Record<string, unknown> : {};
    // A note with no id still has to render and key stably, so the index is the
    // fallback — dropping it would silently lose the note.
    return { id: str(o['id']) || `n${i}`, time: str(o['time']), text: str(o['text']) };
  });
}

/**
 * One row → a corrected entry.
 *
 * ⚠ The flattened `market_regime` / `bias_summary` / … columns and the nested
 * `pre_market_plan` JSONB overlap: the old client writes BOTH, the columns "for
 * analytics" and the blob for everything else. The columns win where they carry
 * a value, because they are what a server-side query would read — but a legacy
 * entry has them empty and its content only in the blob, so the blob is the
 * fallback rather than the other way round.
 */
export function journalEntryFromDb(r: Record<string, unknown>): JournalEntry {
  const plan = normalisePlan((r['pre_market_plan'] ?? {}) as Record<string, unknown>);

  const merged = {
    ...plan,
    marketRegime: str(r['market_regime']) || plan.marketRegime,
    gammaRegime: str(r['gamma_regime']) || plan.gammaRegime,
    overallBias: str(r['overall_bias']) || plan.overallBias,
    marketRegimeAnalysis: str(r['market_regime_analysis']) || plan.marketRegimeAnalysis,
    gammaRegimeAnalysis: str(r['gamma_regime_analysis']) || plan.gammaRegimeAnalysis,
    biasSummary: str(r['bias_summary']) || plan.biasSummary,
  };

  const entry: JournalEntry = {
    date: str(r['date']).slice(0, 10),
    preMarketPlan: merged,
    midSessionNotes: notesFromDb(r['mid_session_notes']),
    eodRecap: recapFromDb(r),
    booleans: (r['booleans'] ?? {}) as Record<string, unknown>,
    disqualifiers: str(r['disqualifiers']),
    originalCalendarDate: null,
    createdAt: r['created_at'] == null ? null : String(r['created_at']),
    readiness: {
      items: (r['readiness_items'] ?? {}) as Record<string, unknown>,
      completedAt: r['readiness_completed_at'] == null
        ? null : String(r['readiness_completed_at']),
    },
    // ⚠ Numbers coerced: these come back from JSONB and a string target would
    // compare and format as text. `locked` is strict — a truthy string must not
    // read as a locked contract.
    sessionContract: (() => {
      const c = (r['session_contract'] ?? {}) as Record<string, unknown>;
      // ⚠ Timestamps pass through AS STORED. They are only ever read back for
      // comparison and display; reformatting one here would make it disagree with
      // the copy the old client wrote, and `dllAcknowledgedAt` is string-compared
      // against a trade's timestamp to flag post-override trades.
      const ts = (v: unknown): string | null =>
        (typeof v === 'string' && v) ? v : null;
      return {
        accountIds: Array.isArray(c['accountIds'])
          ? (c['accountIds'] as unknown[]).filter((x): x is string => typeof x === 'string')
          : [],
        targetProfit: Number(c['targetProfit']) || 0,
        personalDLL: Number(c['personalDLL']) || 0,
        locked: c['locked'] === true,
        lockedAt: ts(c['lockedAt']),
        dllHitAt: ts(c['dllHitAt']),
        dllAcknowledgedAt: ts(c['dllAcknowledgedAt']),
        targetHitAt: ts(c['targetHitAt']),
        targetAcknowledgedAt: ts(c['targetAcknowledgedAt']),
        // ⚠ `Number()` would turn an absent key into NaN and a cleared one into 0 —
        // and 0 is a legitimate override meaning "I made nothing today".
        manualPnl: typeof c['manualPnl'] === 'number' && Number.isFinite(c['manualPnl'])
          ? c['manualPnl'] : null,
      };
    })(),
    recordings: Array.isArray(r['recordings'])
      ? (r['recordings'] as unknown[]).filter((x): x is string => typeof x === 'string')
      : [],
  };

  // ⚠ The correction, applied here so no surface can forget it.
  return applyEodSwap(entry);
}

export interface LoadJournalResult {
  /** Keyed by date — one entry per day, which is the table's primary key. */
  byDate: Record<string, JournalEntry>;
  /**
   * `date → updated_at`, for seeding the stale-write baseline.
   *
   * ⚠ A row whose `updated_at` came back null is OMITTED rather than stored as
   * undefined — `guard.ts` makes the same call and states why: a baseline entry
   * of `undefined` reads back as "never loaded", which makes the row permanently
   * fresh and therefore permanently unguarded.
   */
  updatedAt: Map<string, string>;
  /**
   * date → the RAW  blob, exactly as stored.
   *
   * ⚠ The writer merges over this rather than rebuilding from the typed view, so
   * a key this client has never modelled survives a save. See .
   * Kept OUT of  on purpose — an entry that carried its own raw
   * copy would invite a caller to edit one and save the other.
   */
  /**
   * date → the RAW `pre_market_plan` blob, exactly as stored.
   *
   * ⚠ The writer MERGES over this rather than rebuilding from the typed view, so a
   * key this client has never modelled survives a save — see `planToBlob`. Three
   * round-trip losses shipped before that rule existed.
   *
   * ⚠ Kept OUT of `JournalEntry` deliberately. An entry carrying its own raw copy
   * invites a caller to edit the typed view and save the stale raw one, which is
   * the same shape as the D46 hazard: two representations of one row, and the
   * wrong one reaching the database.
   */
  rawPlans: Map<string, Record<string, unknown>>;
  /**
   * The `session_contract` blob, raw, for the same reason and with a sharper
   * consequence: four of its keys are timestamps written DURING the session
   * (`dllHitAt`, `dllAcknowledgedAt`, `targetHitAt`, `targetAcknowledgedAt`) plus
   * `manualPnl`, and `saveSessionContract` must merge over them rather than write
   * them back from a form that has been open since before the breach.
   */
  rawContracts: Map<string, Record<string, unknown>>;
  pages: number;
}

/**
 * Every journal entry, newest first.
 *
 * ⚠ PAGED. A daily journal reaches 250 entries a year; two or three years of
 * discipline exceeds a single PostgREST page, and the entries that would fall off
 * are the OLDEST — which is exactly the history a review tab exists to look at.
 */
export async function loadJournal(sb: SupabaseClient): Promise<LoadJournalResult> {
  const byDate: Record<string, JournalEntry> = {};
  const updatedAt = new Map<string, string>();
  const rawPlans = new Map<string, Record<string, unknown>>();
  const rawContracts = new Map<string, Record<string, unknown>>();
  let pages = 0;

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('journal_entries')
      .select(JOURNAL_SELECT)
      .order('date', { ascending: false })
      .range(from, from + PAGE_ROWS - 1);
    pages++;

    if (error) {
      throw new Error(
        `loadJournal: page ${pages} failed: ${error.message}. Refusing to return a ` +
          `partial journal — a missing day reads as a day the trader never journalled, ` +
          `which is the opposite of the fact and unrecoverable by looking harder.`,
      );
    }
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) {
      const e = journalEntryFromDb(r);
      if (!e.date) continue;
      byDate[e.date] = e;
      const ts = r['updated_at'];
      if (typeof ts === 'string' && ts) updatedAt.set(e.date, ts);
      const rawPlan = r['pre_market_plan'];
      rawPlans.set(e.date, (rawPlan && typeof rawPlan === 'object')
        ? { ...(rawPlan as Record<string, unknown>) } : {});
      const rawContract = r['session_contract'];
      rawContracts.set(e.date, (rawContract && typeof rawContract === 'object')
        ? { ...(rawContract as Record<string, unknown>) } : {});
    }
    if (data.length < PAGE_ROWS) break;
  }

  return { byDate, updatedAt, rawPlans, rawContracts, pages };
}

/**
 * ONE day's entry, or null when that date has no row.
 *
 * ── WHY THIS EXISTS ALONGSIDE `loadJournal` ──
 *
 * The Day Overview needs a single date's plan and recap for its Plan & Recap
 * card. `loadJournal` pages the entire journal — several hundred rows and every
 * blob on each of them — which is right for the Journal tab, the Calendar and
 * the Coach's Report, all of which move between dates freely. It is the wrong
 * read for one card on a tab that already knows its date.
 *
 * ⚠ READ-ONLY, AND NOTHING MAY WRITE FROM IT. It returns the typed view only —
 * no `updated_at`, no raw blobs — precisely so a caller cannot build a save on
 * top of it. Every journal writer takes its baseline and its raw blob from
 * `loadJournal`'s result, and D62's whole argument is that the typed model is
 * lossy: `selfAssessment` ratings and hypothesis→pattern links do not survive it.
 * A save assembled from this function would silently destroy both.
 *
 * ⚠ A FAILED READ THROWS, matching `loadJournal`. Returning null on error would
 * make "the read broke" indistinguishable from "you did not journal that day",
 * and the card's whole job is to show what the day was for.
 */
export async function loadJournalEntry(
  sb: SupabaseClient,
  date: string,
): Promise<JournalEntry | null> {
  const { data, error } = await sb
    .from('journal_entries')
    .select(JOURNAL_SELECT)
    .eq('date', date)
    .limit(1);

  if (error) throw new Error(`loadJournalEntry(${date}): ${error.message}`);
  if (!data || !data.length) return null;
  return journalEntryFromDb(data[0] as unknown as Record<string, unknown>);
}

/**
 * The entries inside one inclusive date window, oldest first.
 *
 * Written for the Review tab's brief, which quotes a week or a month of journal
 * back to the coach. `loadJournal` would page the ENTIRE book with every prose
 * blob on it to answer a question about seven days.
 *
 * ⚠ NOT PAGED, and that is safe rather than lucky: `date` is the table's primary
 * key, so a calendar month cannot return more than 31 rows against a 1,000-row
 * page. If this is ever pointed at a wider window it needs the paging loop
 * `loadJournal` uses.
 *
 * ⚠ A FAILED READ THROWS, matching both readers above. An empty array would tell
 * the coach the trader journalled nothing that week — a claim, and the wrong one.
 */
export async function loadJournalRange(
  sb: SupabaseClient,
  startDate: string,
  endDate: string,
): Promise<JournalEntry[]> {
  const { data, error } = await sb
    .from('journal_entries')
    .select(JOURNAL_SELECT)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true });

  if (error) {
    throw new Error(`loadJournalRange(${startDate}…${endDate}): ${error.message}`);
  }
  return ((data ?? []) as unknown as Record<string, unknown>[]).map(journalEntryFromDb);
}

/**
 * Just the DATES that have a journal entry.
 *
 * The Calendar puts a dot on days the trader journalled. That is one boolean per
 * cell, and `loadJournal` would page every entry with every blob on it to
 * produce it — hundreds of rows of prose to draw thirty dots.
 *
 * ⚠ PAGED ANYWAY, and for the same reason `loadJournal` is: a daily journal
 * passes a single PostgREST page inside two years, and the rows that fall off
 * are the OLDEST — so an unpaged version would quietly stop dotting the history
 * the Calendar exists to page back through.
 *
 * ⚠ A FAILED READ THROWS. Returning an empty set would draw a calendar with no
 * dots at all, which reads as "you have never journalled" — a claim, and the
 * wrong one.
 */
export async function loadJournalDates(sb: SupabaseClient): Promise<Set<string>> {
  const out = new Set<string>();
  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date')
      .order('date', { ascending: false })
      .range(from, from + PAGE_ROWS - 1);

    if (error) throw new Error(`loadJournalDates: ${error.message}`);
    if (!data || !data.length) break;
    for (const r of data as unknown as Record<string, unknown>[]) {
      const d = r['date'];
      if (typeof d === 'string' && d) out.add(d);
    }
    if (data.length < PAGE_ROWS) break;
  }
  return out;
}
